import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Textarea,
} from '@monksflow/monks-ui';
import { AnimatePresence, motion, type MotionProps } from 'motion/react';
import { FileIcon, PaperclipIcon, PlusIcon, UploadIcon } from 'lucide-react';

import type { BriefArtifactRef, KnowledgeBaseRef } from '../../model/brief-generation.types';
import { KnowledgeBasePicker } from '../KnowledgeBasePicker/KnowledgeBasePicker';
import { BriefIntakeChip } from './BriefIntakeChip';
import { useTranslation } from 'react-i18next';

// Fade on add/remove; `layout` slides the remaining chips into the gap left
// by a removed one.
const CHIP_MOTION = {
  layout: true,
  initial: { opacity: 0 },
  animate: { opacity: 1 },
  exit: { opacity: 0 },
  transition: { duration: 0.15, ease: 'easeOut' },
} satisfies MotionProps;

export interface BriefIntakeComposerProps {
  instructions: string;
  onInstructionsChange: (value: string) => void;
  files: File[];
  artifacts: BriefArtifactRef[];
  onRemoveFile: (index: number) => void;
  onRemoveArtifact: (id: string) => void;
  onUploadFile: () => void;
  onBrowseArtifacts: () => void;
  knowledgeBases: KnowledgeBaseRef[];
  onToggleKnowledgeBase: (kb: KnowledgeBaseRef) => void;
  onClearKnowledgeBases: () => void;
}

export const BriefIntakeComposer = ({
  instructions,
  onInstructionsChange,
  files,
  artifacts,
  onRemoveFile,
  onRemoveArtifact,
  onUploadFile,
  onBrowseArtifacts,
  knowledgeBases,
  onToggleKnowledgeBase,
  onClearKnowledgeBases,
}: BriefIntakeComposerProps) => {
  const { t } = useTranslation('briefs');

  return (
    <div className="flex flex-col gap-2 rounded-lg border border-border bg-input p-2 focus-within:ring-1 focus-within:ring-ring">
      <ul className="flex flex-wrap gap-1.5 empty:hidden">
        <AnimatePresence initial={false}>
          {files.map((file, index) => (
            <motion.li key={`file-${file.name}-${file.size}`} {...CHIP_MOTION}>
              <BriefIntakeChip
                icon={FileIcon}
                label={file.name}
                onRemove={() => onRemoveFile(index)}
              />
            </motion.li>
          ))}
          {artifacts.map((artifact) => (
            <motion.li key={`artifact-${artifact.id}`} {...CHIP_MOTION}>
              <BriefIntakeChip
                icon={PaperclipIcon}
                label={artifact.name}
                onRemove={() => onRemoveArtifact(artifact.id)}
              />
            </motion.li>
          ))}
        </AnimatePresence>
      </ul>

      <Textarea
        aria-label={t('briefGeneration.briefIntakeComposer.goals')}
        placeholder={t('briefGeneration.briefIntakeComposer.describeWhatYouWantTo')}
        value={instructions}
        onChange={(event) => onInstructionsChange(event.target.value)}
        rows={3}
        className="max-h-[240px] resize-none overflow-y-auto border-0 bg-transparent p-1 shadow-none focus-visible:ring-0 focus-visible:ring-offset-0"
      />

      <div className="flex items-center gap-1">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="sm"
              aria-label={t('briefGeneration.briefIntakeComposer.addContext')}
            >
              <PlusIcon className="size-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start">
            <DropdownMenuItem onSelect={onUploadFile}>
              <UploadIcon className="size-4" />
              {t('briefGeneration.briefIntakeComposer.uploadDocuments')}
            </DropdownMenuItem>
            <DropdownMenuItem onSelect={onBrowseArtifacts}>
              <PaperclipIcon className="size-4" />
              {t('briefGeneration.briefIntakeComposer.attachArtifacts')}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>

        <KnowledgeBasePicker
          selected={knowledgeBases}
          onToggle={onToggleKnowledgeBase}
          onClear={onClearKnowledgeBases}
        />
      </div>
    </div>
  );
};
